import { useMutation, useQuery } from '@apollo/client';
import {
  CreatePriceChangesSubscriptionInput,
  CreatePriceChangesSubscriptionMutation,
  CreatePriceChangesSubscriptionMutationVariables,
  DeletePriceChangesSubscriptionMutation,
  DeletePriceChangesSubscriptionMutationVariables,
  PriceChangesSubscriptionQuery,
  PriceChangesSubscriptionQueryVariables,
  PriceChangesSubscriptionsQuery,
  UpdatePriceChangesSubscriptionMutation,
  UpdatePriceChangesSubscriptionMutationVariables,
} from 'types/apollo';
import CREATE_PRICE_CHANGES_SUBSCRIPTION from './create-price-changes-subscription.gql';
import DELETE_PRICE_CHANGES_SUBSCRIPTION from './delete-price-changes-subscription.gql';
import GET_PRICE_CHANGES_SUBSCRIPTION from './price-changes-subscription.gql';
import GET_PRICE_CHANGES_SUBSCRIPTIONS from './price-changes-subscriptions.gql';
import UPDATE_PRICE_CHANGES_SUBSCRIPTION from './update-price-changes-subscription.gql';

export interface PriceChangesActive {
  [ratio: string]: boolean;
  80: boolean;
  85: boolean;
  90: boolean;
  95: boolean;
}

export type SubscriptionRoute = Omit<
  CreatePriceChangesSubscriptionInput,
  'changes' | 'minPrice'
>;

const defaultPriceChangesActive = {
  80: false,
  85: false,
  90: false,
  95: false,
};

function getSearchKey({
  adultCount,
  childCount,
  infantCount,
  cabinClass,
  isNonstop,
  itineraries,
  tripType,
}: SubscriptionRoute) {
  return [
    tripType,
    cabinClass,
    adultCount,
    childCount,
    infantCount,
    isNonstop,
    ...itineraries.map(
      ({ departureLocationCode, arrivalLocationCode, departureDate }) =>
        [departureDate, departureLocationCode, arrivalLocationCode].join(''),
    ),
  ].join('');
}

function useCreatePriceChangesSubscription() {
  const [createSubscription] = useMutation<
    CreatePriceChangesSubscriptionMutation,
    CreatePriceChangesSubscriptionMutationVariables
  >(CREATE_PRICE_CHANGES_SUBSCRIPTION, {
    refetchQueries: [GET_PRICE_CHANGES_SUBSCRIPTIONS],
    update(cache, { data }) {
      const { createPriceChangesSubscription } = data!;
      const { searchKey } = createPriceChangesSubscription!;

      cache.writeQuery({
        query: GET_PRICE_CHANGES_SUBSCRIPTION,
        variables: { searchKey },
        data: { priceChangesSubscription: createPriceChangesSubscription },
      });
    },
  });
  return createSubscription;
}

function useDeletePriceChangesSubscription(searchKey: string) {
  const [deleteSubscription] = useMutation<
    DeletePriceChangesSubscriptionMutation,
    DeletePriceChangesSubscriptionMutationVariables
  >(DELETE_PRICE_CHANGES_SUBSCRIPTION, {
    variables: { searchKey },
  });
  return deleteSubscription;
}

function usePriceChangesSubscription(searchKey: string) {
  const { data, ...rest } = useQuery<
    PriceChangesSubscriptionQuery,
    PriceChangesSubscriptionQueryVariables
  >(GET_PRICE_CHANGES_SUBSCRIPTION, {
    variables: { searchKey },
    fetchPolicy: 'cache-and-network',
  });
  return {
    ...rest,
    subscription: data?.priceChangesSubscription?.isDeleted
      ? null
      : data?.priceChangesSubscription,
  };
}

function usePriceChangesSubscriptions() {
  const { data, ...rest } = useQuery<PriceChangesSubscriptionsQuery>(
    GET_PRICE_CHANGES_SUBSCRIPTIONS,
    { fetchPolicy: 'cache-and-network' },
  );
  return {
    ...rest,
    subscriptions:
      data?.priceChangesSubscriptions.edges?.filter(
        ({ node: { isDeleted } }) => !isDeleted,
      ) || [],
  };
}

function useUpdatePriceChangesSubscription() {
  const [updateSubscription] = useMutation<
    UpdatePriceChangesSubscriptionMutation,
    UpdatePriceChangesSubscriptionMutationVariables
  >(UPDATE_PRICE_CHANGES_SUBSCRIPTION);
  return updateSubscription;
}

export {
  defaultPriceChangesActive,
  getSearchKey,
  useCreatePriceChangesSubscription,
  useDeletePriceChangesSubscription,
  usePriceChangesSubscription,
  usePriceChangesSubscriptions,
  useUpdatePriceChangesSubscription,
};
